import React, { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import './DataTable.css';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#1e293b',
    color: theme.palette.common.white,
    position: 'sticky',
    top: 0,
    zIndex: 1,
    fontSize: '0.9rem',
    fontWeight: '600',
    borderBottom: '2px solid #334155',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.85rem',
    color: '#e2e8f0',
    borderBottom: '1px solid #334155',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#1e293b',
  },
  '&:nth-of-type(even)': {
    backgroundColor: '#0f172a',
  },
  '&:hover': {
    backgroundColor: '#334155',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#1e293b',
    color: '#e2e8f0',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: '#334155',
      borderWidth: '2px',
    },
    '&:hover fieldset': {
      borderColor: '#475569',
      boxShadow: '0 0 0 1px rgba(59, 130, 246, 0.3)',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3b82f6',
      boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#94a3b8',
    fontWeight: '500',
    '&.Mui-focused': {
      color: '#3b82f6',
    },
  },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': {
    color: '#94a3b8',
    transition: 'color 0.3s ease',
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
    minWidth: '280px',
  },
  '& .MuiMenuItem-root': {
    color: '#e2e8f0',
    '&:hover': {
      backgroundColor: '#334155',
    },
  },
  '& .MuiCheckbox-root': {
    color: '#94a3b8',
    '&.Mui-checked': {
      color: '#3b82f6',
    },
  },
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(59, 130, 246, 0.2)',
  color: '#93c5fd',
  border: '1px solid rgba(59, 130, 246, 0.3)',
  '& .MuiChip-deleteIcon': {
    color: '#93c5fd',
    '&:hover': {
      color: '#dbeafe',
    },
  },
}));

export default function CustomizedTables() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchColumns, setSearchColumns] = useState({
    dateTime: true,
    voltage: true,
    current: true,
    power: true,
    energy: true,
    frequency: true,
    powerFactor: true,
  });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [searchHistory, setSearchHistory] = useState([]);

  const columnLabels = {
    dateTime: 'Date/Time',
    voltage: 'Voltage',
    current: 'Current',
    power: 'Power',
    energy: 'Energy',
    frequency: 'Frequency',
    powerFactor: 'Power Factor',
  };

  // Helper function to format date safely
  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
  };

  // Enhanced filter function with column-specific search
  const filterData = useMemo(() => {
    return (data, query, columns) => {
      if (!query.trim()) return data;
      
      const lowercaseQuery = query.toLowerCase();
      return data.filter((row) => {
        return Object.entries(columns).some(([columnKey, isEnabled]) => {
          if (!isEnabled) return false;
          const value = row[columnKey];
          return value && value.toString().toLowerCase().includes(lowercaseQuery);
        });
      });
    };
  }, []);

  // Handle search input change with debouncing
  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    
    // Add to search history if it's a meaningful search (>2 chars) and not duplicate
    if (query.length > 2 && !searchHistory.includes(query)) {
      setSearchHistory(prev => [query, ...prev.slice(0, 4)]); // Keep last 5 searches
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setFilteredRows(rows);
  };

  // Handle filter menu
  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  // Handle column filter toggle
  const handleColumnToggle = (columnKey) => {
    setSearchColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Apply filters whenever search query or column filters change
  useEffect(() => {
    setFilteredRows(filterData(rows, searchQuery, searchColumns));
  }, [rows, searchQuery, searchColumns, filterData]);

  // Get active filter count
  const activeFiltersCount = Object.values(searchColumns).filter(Boolean).length;
  const totalColumns = Object.keys(searchColumns).length;

  // all data get backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_ENDPOINT_HTTP + '/api/power/all');
        const data = await response.json();

        const formattedData = data.map((item) =>
          createData(
            formatDateTime(item.timestamp),
            item.voltage,
            item.current,
            item.power,
            item.energy,
            item.frequency,
            item.powerFactor
          )
        );

        setRows(formattedData);
        setFilteredRows(formattedData); // Initialize filtered rows
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  // WebSocket connection 
  useEffect(() => {
    const socket = new WebSocket(process.env.REACT_APP_ENDPOINT_WS); 
    
    socket.onmessage = (event) => {
      const newData = JSON.parse(event.data);
      const newRow = createData(
        formatDateTime(newData.timestamp),
        newData.voltage,
        newData.current,
        newData.power,
        newData.energy,
        newData.frequency,
        newData.powerFactor
      );

      setRows((prevRows) => [newRow, ...prevRows]);
    };

    return () => socket.close();
  }, []);

  function createData(dateTime, voltage, current, power, energy, frequency, powerFactor) {
    return { dateTime, voltage, current, power, energy, frequency, powerFactor };
  }

  return (
    <section className="cards grid3">
      <div className="cardBox">
        <div className="tableBox">
          <div className="bg-gradient-to-r from-blue-900 to-emerald-900 p-4 rounded-t-xl">
            <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2 mb-4">
              <svg className="w-6 h-6 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Power Monitoring Data
            </h2>
            
            {/* Enhanced Search Bar */}
            <Box sx={{ position: 'relative', mb: 2 }}>
              <StyledTextField
                fullWidth
                variant="outlined"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Try searching for voltage values, dates, or any data..."
                size="medium"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '1.2rem' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {searchQuery && (
                          <IconButton
                            size="small"
                            onClick={handleClearSearch}
                            sx={{ 
                              color: '#94a3b8',
                              '&:hover': { 
                                color: '#e2e8f0',
                                backgroundColor: 'rgba(148, 163, 184, 0.1)' 
                              }
                            }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={handleFilterMenuOpen}
                          sx={{ 
                            color: activeFiltersCount === totalColumns ? '#94a3b8' : '#3b82f6',
                            '&:hover': { 
                              color: '#e2e8f0',
                              backgroundColor: 'rgba(59, 130, 246, 0.1)' 
                            }
                          }}
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  backgroundColor: 'rgba(30, 41, 59, 0.9)',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.95rem',
                  }
                }}
              />
              
              {/* Search Results Count */}
              {searchQuery && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    mt: 0.5,
                    color: '#94a3b8',
                    fontSize: '0.75rem'
                  }}
                >
                  {filteredRows.length} of {rows.length} records found
                </Typography>
              )}
            </Box>

            {/* Active Filters Display */}
            {activeFiltersCount < totalColumns && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', alignSelf: 'center', mr: 1 }}>
                  Searching in:
                </Typography>
                {Object.entries(searchColumns)
                  .filter(([, enabled]) => enabled)
                  .map(([columnKey]) => (
                    <StyledChip
                      key={columnKey}
                      label={columnLabels[columnKey]}
                      size="small"
                      onDelete={() => handleColumnToggle(columnKey)}
                    />
                  ))}
              </Box>
            )}

            {/* Filter Menu */}
            <StyledMenu
              anchorEl={filterMenuAnchor}
              open={Boolean(filterMenuAnchor)}
              onClose={handleFilterMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem disabled>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#e2e8f0' }}>
                  Search in Columns
                </Typography>
              </MenuItem>
              <Divider sx={{ backgroundColor: '#334155', my: 1 }} />
              
              {Object.entries(columnLabels).map(([columnKey, label]) => (
                <MenuItem 
                  key={columnKey}
                  onClick={() => handleColumnToggle(columnKey)}
                  sx={{ py: 0.5 }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={searchColumns[columnKey]}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ color: '#e2e8f0' }}>
                        {label}
                      </Typography>
                    }
                    sx={{ margin: 0 }}
                  />
                </MenuItem>
              ))}
              
              <Divider sx={{ backgroundColor: '#334155', my: 1 }} />
              <MenuItem 
                onClick={() => setSearchColumns(Object.keys(searchColumns).reduce((acc, key) => ({ ...acc, [key]: true }), {}))}
                sx={{ justifyContent: 'center' }}
              >
                <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 'bold' }}>
                  Select All
                </Typography>
              </MenuItem>
            </StyledMenu>
          </div>
          <TableContainer 
            component={Paper} 
            className="tableContainer rounded-t-none" 
            style={{ 
              maxHeight: 'calc(100vh - 350px)', // Adjusted for enhanced search bar
              overflowY: 'auto',
              background: 'linear-gradient(to bottom right, #0f172a, #1e293b)',
              borderTop: 'none'
            }}
          >
            <Table stickyHeader sx={{ minWidth: 650 }} aria-label="customized table">
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center">Date/Time</StyledTableCell>
                  <StyledTableCell align="center">Voltage (V)</StyledTableCell>
                  <StyledTableCell align="center">Current (A)</StyledTableCell>
                  <StyledTableCell align="center">Power (W)</StyledTableCell>
                  <StyledTableCell align="center">Energy (kWh)</StyledTableCell>
                  <StyledTableCell align="center">Frequency (Hz)</StyledTableCell>
                  <StyledTableCell align="center">Power Factor</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row, index) => (
                  <StyledTableRow key={`${row.dateTime}-${index}`}>
                    <StyledTableCell component="th" scope="row" align="center">
                      {row.dateTime}
                    </StyledTableCell>
                    <StyledTableCell align="center" className="font-mono">{row.voltage}</StyledTableCell>
                    <StyledTableCell align="center" className="font-mono">{row.current}</StyledTableCell>
                    <StyledTableCell align="center" className="font-mono">{row.power}</StyledTableCell>
                    <StyledTableCell align="center" className="font-mono">{row.energy}</StyledTableCell>
                    <StyledTableCell align="center" className="font-mono">{row.frequency}</StyledTableCell>
                    <StyledTableCell align="center" className="font-mono">{row.powerFactor}</StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </section>
  );
}